import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { BootErrorBoundary, markBootSuccess } from "@/components/BootErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Global PWA install-prompt catcher. Chrome/Android fires
// `beforeinstallprompt` once, very early — often BEFORE the InstallPrompt
// component has mounted (e.g. admins landing on /admin first). Capture the
// event on `window` so any later-mounted component can still consume it.
if (typeof window !== "undefined") {
    window.__deferredInstallPrompt = null;
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        window.__deferredInstallPrompt = e;
        window.dispatchEvent(new CustomEvent("micm-install-available"));
    });
    window.addEventListener("appinstalled", () => {
        window.__deferredInstallPrompt = null;
    });
}

root.render(
  <React.StrictMode>
    <BootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </BootErrorBoundary>
  </React.StrictMode>,
);

// v1.27.5 — If we're still standing 4 seconds after render, the boot was
// successful; clear the auto-reload counter so a future genuine restart
// isn't mistaken for a loop.
setTimeout(markBootSuccess, 4000);

// v1.24.8 — Safety net: on every page load, compare the bundle's compiled
// APP_VERSION with the backend's /api/version. If out of date, purge SW +
// caches and hard-reload once so stale PWAs never linger on old builds.
import { checkForUpdateAndReload, installSwUpdateListener } from "@/lib/autoUpdate";
checkForUpdateAndReload();
installSwUpdateListener();

// Register service worker for PWA share target (WhatsApp/Android → Upload Slip)
// Also auto-reload installed PWAs whenever a new SW activates, so users always
// see the latest UI without having to uninstall/reinstall the app.
// v1.27.47 — Pass APP_VERSION as `?v=…` on the SW registration URL. Every
// deploy that bumps APP_VERSION therefore produces a NEW SW script URL,
// which browsers treat as a new SW → install → activate → purge all
// caches → reload the page. This is what makes the "increment the SW
// version on every deploy" guarantee automatic rather than manual.
import { APP_VERSION as _APP_VERSION_FOR_SW } from "@/lib/version";
if ("serviceWorker" in navigator && window.location.protocol === "https:") {
    window.addEventListener("load", () => {
        navigator.serviceWorker
            .register(`/service-worker.js?v=${encodeURIComponent(_APP_VERSION_FOR_SW)}`, { updateViaCache: "none" })
            .then((reg) => {
                // Force an update check on every load AND every 30 minutes.
                reg.update().catch(() => {});
                setInterval(() => reg.update().catch(() => {}), 30 * 60 * 1000);
                // When a new SW is found, tell it to skip waiting so it activates immediately.
                reg.addEventListener("updatefound", () => {
                    const nw = reg.installing;
                    if (!nw) return;
                    nw.addEventListener("statechange", () => {
                        if (nw.state === "installed" && navigator.serviceWorker.controller) {
                            nw.postMessage({ type: "SKIP_WAITING" });
                        }
                    });
                });
            })
            .catch(() => {});

        // Auto-reload once the new SW takes control (skipWaiting → activate → controllerchange)
        let refreshing = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (refreshing) return;
            refreshing = true;
            window.location.reload();
        });

        // Also honour SW_UPDATED broadcasts sent from the activate handler.
        navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data && event.data.type === "SW_UPDATED") {
                if (refreshing) return;
                refreshing = true;
                window.location.reload();
            }
        });
    });
}
